import { useState } from "react";
import axios from "axios";
import { Building2, Mail, MapPin, Send, CheckCircle2, Loader2 } from "lucide-react";
import { MaskedLine, FadeUp } from "./Reveal";

const API = "/api";

const DETAILS = [
  { icon: Building2, label: "Company", value: "Kritajna Ventures LLP", testId: "contact-detail-company" },
  { icon: Mail, label: "Email", value: "admin@kritajnaventures.com", testId: "contact-detail-email", href: "mailto:admin@kritajnaventures.com" },
  { icon: MapPin, label: "Location", value: "Kerala, India", testId: "contact-detail-location" },
];

const inputCls =
  "w-full rounded-xl border border-navy/15 bg-white px-4 py-3.5 text-sm text-navy placeholder:text-navy/35 outline-none transition-[border-color,box-shadow] duration-300 focus:border-peri focus:ring-4 focus:ring-peri/15";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("sent");
    } catch (err) {
      setStatus("idle");
      setError("Something went wrong while sending your message. Please try again or email us directly.");
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-navy text-mist py-28 lg:py-40 overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-48 left-1/3 w-[36rem] h-[36rem] rounded-full bg-violet/25 blur-[150px]" />
      <div className="pointer-events-none absolute -bottom-40 -right-32 w-[30rem] h-[30rem] rounded-full bg-peri/20 blur-[140px]" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-16">
        <div className="lg:col-span-5">
          <FadeUp>
            <p className="uppercase tracking-[0.28em] text-xs sm:text-sm font-bold text-peri flex items-center gap-3">
              <span className="inline-block w-10 h-px bg-peri" />
              Contact Us
            </p>
          </FadeUp>
          <h2
            data-testid="contact-heading"
            className="mt-8 font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight"
          >
            <MaskedLine delay={0.1}>Partner</MaskedLine>
            <MaskedLine delay={0.22}>
              <span className="text-grad-light">With Us</span>
            </MaskedLine>
          </h2>
          <FadeUp delay={0.35}>
            <p data-testid="contact-subheadline" className="mt-7 text-base sm:text-lg leading-relaxed text-mist/70 max-w-md">
              Whether you are looking to lease commercial space, integrate our SaaS solutions,
              or explore trade partnerships, we invite you to connect.
            </p>
          </FadeUp>

          <div className="mt-12 flex flex-col gap-6">
            {DETAILS.map((d, i) => (
              <FadeUp key={d.label} delay={0.45 + i * 0.1}>
                <div data-testid={d.testId} className="flex items-center gap-5 group">
                  <span className="grid place-items-center w-12 h-12 rounded-2xl border border-mist/15 bg-mist/5 text-peri transition-colors duration-300 group-hover:bg-brand-gradient group-hover:text-white group-hover:border-transparent">
                    <d.icon className="w-5 h-5" />
                  </span>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.24em] text-mist/45">{d.label}</p>
                    {d.href ? (
                      <a href={d.href} className="text-base sm:text-lg font-semibold text-mist hover:text-grad-light transition-colors duration-300">
                        {d.value}
                      </a>
                    ) : (
                      <p className="text-base sm:text-lg font-semibold text-mist">{d.value}</p>
                    )}
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>

        <FadeUp delay={0.3} className="lg:col-span-7">
          <div className="rounded-[2rem] bg-mist text-navy p-8 sm:p-12 shadow-[0_40px_90px_rgba(10,16,36,0.5)]">
            {status === "sent" ? (
              <div data-testid="contact-success-message" className="py-14 text-center">
                <span className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-brand-gradient text-white">
                  <CheckCircle2 className="w-8 h-8" />
                </span>
                <h3 className="mt-7 font-display text-3xl text-navy">Message received.</h3>
                <p className="mt-3 text-navy/60 max-w-sm mx-auto">
                  Thank you for reaching out to Kritajna Ventures LLP. Our team will get back to
                  you shortly.
                </p>
                <button
                  data-testid="contact-send-another-button"
                  onClick={() => {
                    setForm({ name: "", email: "", subject: "", message: "" });
                    setStatus("idle");
                  }}
                  className="mt-8 rounded-full border border-navy/20 px-7 py-3 text-sm font-semibold text-navy transition-colors duration-300 hover:bg-navy hover:text-mist"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="flex flex-col gap-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-xs font-bold uppercase tracking-[0.18em] text-navy/60 mb-2">Name</label>
                    <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={update("name")} placeholder="Your full name" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-bold uppercase tracking-[0.18em] text-navy/60 mb-2">Email</label>
                    <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={update("email")} placeholder="you@company.com" className={inputCls} />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-xs font-bold uppercase tracking-[0.18em] text-navy/60 mb-2">Subject</label>
                  <input id="contact-subject" data-testid="contact-subject-input" required value={form.subject} onChange={update("subject")} placeholder="Leasing, SaaS, or trade partnerships" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-xs font-bold uppercase tracking-[0.18em] text-navy/60 mb-2">Message</label>
                  <textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={update("message")} placeholder="Tell us how we can work together" className={`${inputCls} resize-none`} />
                </div>
                {error && (
                  <p data-testid="contact-error-message" className="text-sm font-medium text-violet">{error}</p>
                )}
                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={status === "sending"}
                  className="group mt-2 inline-flex items-center justify-center gap-3 rounded-full bg-brand-gradient px-8 py-4 text-sm font-bold text-white shadow-[0_14px_32px_rgba(130,24,103,0.35)] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-70 disabled:translate-y-0"
                >
                  {status === "sending" ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

export default Contact;
